import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { fieldLabelResolver } from './fieldPathLabel';
import {
  conditionSummary,
  ICondition,
  isUnary,
  newCondition,
  OPERATOR_LABEL,
  ConditionOperator,
  OperandKind,
} from './editorModel';
import { SelectorPicker } from './SelectorPicker';
import { useArnoSelectorCatalog } from '../../hooks/reactQuerys/ArnoAdminQueries';
import { compareForPreview } from './conditionPreview';

/**
 * Bedingung zusammenklicken (#1245).
 *
 * Kein JSON-Editor: gewählt wird die Quelle (eine Frage des Baums oder ein
 * DiNo-Feld), dann der Vergleich, dann der Wert. Bei „ist leer" verschwindet das
 * Wertfeld, weil dort nichts hingehört.
 *
 * Unter der Zeile steht die Bedingung im Wortlaut — in derselben Sprache, in der
 * der Ablaufviewer sie später zeigt.
 */

interface Props {
  condition: ICondition | null | undefined;
  onChange: (condition: ICondition | null) => void;
  questions: { questionId: string; prompt: string; answerType: string }[];
  disabled?: boolean;
  /** Ohne Bedingung darf die Zeile ganz verschwinden (Abschnitt, bedingter Text). */
  allowNone?: boolean;
  /** Vorgang fuer die Wertvorschau; ohne ihn bleibt die Bedingung eine Regel ohne Beispiel. */
  idLegalTransaction?: number | null;
}

const SOURCE_LABEL: Record<OperandKind, string> = {
  question: 'Antwort auf eine Frage',
  selector: 'Wert aus dem Vorgang',
  constant: 'Fester Text',
};

export const ConditionBuilder: React.FC<Props> = ({
  condition,
  onChange,
  questions,
  disabled,
  allowNone = true,
  idLegalTransaction = null,
}) => {
  // Derselbe Katalog wie in der Feldauswahl — react-query liefert ihn aus dem Cache, es
  // entsteht keine zweite Abfrage je Bedingung.
  const { selectorCatalog } = useArnoSelectorCatalog(idLegalTransaction);
  const fieldLabel = useMemo(
    () => fieldLabelResolver(selectorCatalog),
    [selectorCatalog]
  );
  const catalogByPath = useMemo(() => {
    const map = new Map<string, { label: string; sample: string | null }>();
    for (const group of selectorCatalog?.groups ?? []) {
      for (const field of group.fields) {
        map.set(field.path, { label: field.label, sample: field.sample });
      }
    }
    return map;
  }, [selectorCatalog]);
  const active = condition ?? null;
  const question =
    active?.left.type === 'question'
      ? questions.find(item => item.questionId === active.left.questionId)
      : undefined;
  const booleanAnswer = !question || question.answerType === 'BOOLEAN';

  const update = (patch: Partial<ICondition>) =>
    onChange({ ...(active ?? newCondition()), ...patch });

  const updateLeft = (type: OperandKind) =>
    onChange({
      ...(active ?? newCondition()),
      left:
        type === 'question'
          ? { type, questionId: questions[0]?.questionId ?? '' }
          : type === 'selector'
            ? { type, selector: '' }
            : { type, value: '' },
    });

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
        bgcolor: 'background.default',
      }}
    >
      <Stack direction='row' spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
        <TextField
          size='small'
          select
          label='Bedingung'
          value={active ? 'yes' : 'no'}
          onChange={event =>
            onChange(event.target.value === 'yes' ? newCondition() : null)
          }
          disabled={disabled || !allowNone}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value='no'>ohne Bedingung</MenuItem>
          <MenuItem value='yes'>nur wenn …</MenuItem>
        </TextField>

        {active && (
          <>
            <TextField
              size='small'
              select
              label='Quelle'
              value={active.left.type}
              onChange={event => updateLeft(event.target.value as OperandKind)}
              disabled={disabled}
              sx={{ minWidth: 150 }}
            >
              {(['question', 'selector', 'constant'] as OperandKind[]).map(
                kind => (
                  <MenuItem key={kind} value={kind}>
                    {SOURCE_LABEL[kind]}
                  </MenuItem>
                )
              )}
            </TextField>

            {active.left.type === 'question' && (
              <TextField
                size='small'
                select
                label='Frage'
                value={active.left.questionId ?? ''}
                onChange={event =>
                  update({
                    left: { type: 'question', questionId: event.target.value },
                  })
                }
                disabled={disabled}
                sx={{ minWidth: 260 }}
                helperText={
                  questions.length === 0
                    ? 'Der Baum hat noch keine Frage.'
                    : undefined
                }
              >
                {questions.map(item => (
                  <MenuItem key={item.questionId} value={item.questionId}>
                    {item.prompt}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {active.left.type === 'selector' && (
              <Box sx={{ minWidth: 320, flexGrow: 1 }}>
                <SelectorPicker
                  value={active.left.selector ?? ''}
                  onChange={selector =>
                    update({ left: { type: 'selector', selector } })
                  }
                  disabled={disabled}
                  label='Datenfeld'
                  idLegalTransaction={idLegalTransaction}
                />
              </Box>
            )}

            {active.left.type === 'constant' && (
              <TextField
                size='small'
                label='Fester Wert'
                value={active.left.value ?? ''}
                onChange={event =>
                  update({
                    left: { type: 'constant', value: event.target.value },
                  })
                }
                disabled={disabled}
                sx={{ minWidth: 200 }}
              />
            )}

            <TextField
              size='small'
              select
              label='Vergleich'
              value={active.operator}
              onChange={event =>
                update({ operator: event.target.value as ConditionOperator })
              }
              disabled={disabled}
              sx={{ minWidth: 160 }}
            >
              {(Object.keys(OPERATOR_LABEL) as ConditionOperator[]).map(
                operator => (
                  <MenuItem key={operator} value={operator}>
                    {OPERATOR_LABEL[operator]}
                  </MenuItem>
                )
              )}
            </TextField>

            {!isUnary(active.operator) &&
              (active.left.type === 'question' && booleanAnswer ? (
                <TextField
                  size='small'
                  select
                  label='Wert'
                  value={active.right}
                  onChange={event => update({ right: event.target.value })}
                  disabled={disabled}
                  sx={{ minWidth: 120 }}
                >
                  <MenuItem value='true'>Ja</MenuItem>
                  <MenuItem value='false'>Nein</MenuItem>
                </TextField>
              ) : (
                <TextField
                  size='small'
                  label='Wert'
                  value={active.right}
                  onChange={event => update({ right: event.target.value })}
                  disabled={disabled}
                  sx={{ minWidth: 180 }}
                />
              ))}
          </>
        )}
      </Stack>

      {active && (
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          <Typography variant='caption' color='text.secondary'>
            {conditionSummary(active, questions, fieldLabel)}
          </Typography>
          {(() => {
            // Der gemessene Wert aus dem Vorschauvorgang — nur beim Feldvergleich, denn eine
            // Frage beantwortet erst die Bearbeiterin.
            if (active.left.type !== 'selector' || !active.left.selector) {
              return null;
            }
            const eintrag = catalogByPath.get(active.left.selector);
            if (!eintrag) {
              return null;
            }
            const urteil = compareForPreview(
              eintrag.sample,
              active.operator,
              active.right
            );
            return (
              <Stack
                direction='row'
                spacing={1}
                alignItems='center'
                flexWrap='wrap'
              >
                <Typography variant='caption' color='text.secondary'>
                  Im gewählten Vorgang:
                </Typography>
                <Chip
                  size='small'
                  variant='outlined'
                  label={
                    eintrag.sample ? eintrag.sample : 'Kein Wert vorhanden'
                  }
                />
                {urteil !== null && (
                  <Chip
                    size='small'
                    color={urteil ? 'success' : 'default'}
                    variant={urteil ? 'filled' : 'outlined'}
                    label={urteil ? 'trifft zu' : 'trifft nicht zu'}
                  />
                )}
              </Stack>
            );
          })()}
        </Stack>
      )}
    </Box>
  );
};
